import {
  Card,
  CardClose,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  LoadingSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@ais-ui/utils';
import {
  PiAnchor,
  PiArrowsClockwiseFill,
  PiArrowSquareOut,
} from 'react-icons/pi';
import { openPopupOnSecondaryScreen, PopupType } from '@ais-ui/popups';
import { PortDetailsPanel, PortIdentification } from './PortDetailsPanel';
import { PortActions } from './PortActions';
import { useDrawPort } from './useDrawPort';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { MissingPermission } from '../../components/MissingPermission';
import { CENTERCOMPONENTS } from '../Map/MapPanel';
import { useMapPanelContext } from '../Map/MapPanel';
import { useRoutes } from '../Routes/RoutesContext';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { NextPortArrivalWithDate, useNextportPortGet } from '@ais-ui/api';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useTimeUntil } from '../conversions/useTimeUntil';
import { Port } from '../Map/AISMapContext';
import { useSettings } from '../Settings/SettingsProvider';

interface TargetDetailsMapProps {
  className?: string;
  style?: React.CSSProperties;
}

interface PortCardStackProps {
  className?: string;
  style?: React.CSSProperties;
}

interface PortNextArrivalSummaryProps {
  port: Port;
}

/**
 * "Inbound / Next arrival" stat row shown under the port header in nextport
 * mode. Reuses `useNextportPortGet` — same query key as `PortDetailsPanel`'s
 * fetch, so React Query dedupes it into a single request.
 */
const PortNextArrivalSummary = ({ port }: PortNextArrivalSummaryProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { settings } = useSettings();
  const inclAnchorages =
    settings['ribbonPlugin.settings']?.nextport_incl_anchorages ?? false;
  const { data, isLoading } = useNextportPortGet(port.locode, inclAnchorages);

  const records = data?.records ?? [];

  const nextArrival = records.reduce<NextPortArrivalWithDate | undefined>(
    (soonest, arrival) => {
      if (!arrival.eta) return soonest;
      if (!soonest?.eta) return arrival;
      return new Date(arrival.eta) < new Date(soonest.eta) ? arrival : soonest;
    },
    undefined,
  );

  const etaCountdown = useTimeUntil(nextArrival?.eta);

  if (isLoading) {
    return <LoadingSpinner size="small" />;
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Inbound
        </p>
        <p className="mt-1 truncate text-sm font-semibold">
          {records.length} vessels tracked
        </p>
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Next arrival
        </p>
        <p className="mt-1 truncate text-sm font-semibold">
          {nextArrival?.eta
            ? `${formatDateTimeWithTimeZone(nextArrival.eta, true)}${
                etaCountdown ? ` · ${etaCountdown}` : ''
              }`
            : '-'}
        </p>
      </div>
      <p className="col-span-2 text-xs text-muted-foreground">
        {inclAnchorages
          ? 'Includes anchorage areas'
          : 'Excludes anchorage areas'}
      </p>
    </div>
  );
};

const PortCardStack = ({ className, style }: PortCardStackProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { selectedPorts, setSelectedPorts, portSelectionMode } =
    useSelectionState();
  const goToNextCard = () => {
    if (currentIndex < selectedPorts.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const permissions = usePermissions();

  const handleOpenPopup = useCallback(() => {
    const currentPort = selectedPorts[currentIndex];
    if (!currentPort) return;

    const params: Record<string, string | number> = {
      portId: Number(currentPort.id),
      portName: currentPort.name,
      portLocode: currentPort.locode,
      initialTab: 'nextport',
    };

    openPopupOnSecondaryScreen(PopupType.Routes, params);
  }, [selectedPorts, currentIndex]);

  const multiplePortsSelected = useMemo(() => {
    return selectedPorts.length > 1;
  }, [selectedPorts]);

  const nextPortName = useMemo(() => {
    if (!multiplePortsSelected) {
      return '';
    }
    if (currentIndex < selectedPorts.length - 1) {
      const nextSelectedPort = selectedPorts[currentIndex + 1];
      return nextSelectedPort.name;
    } else {
      const nextSelectedPort = selectedPorts[0];
      return nextSelectedPort.name;
    }
  }, [currentIndex, multiplePortsSelected, selectedPorts]);

  const currentPort = useMemo(() => {
    if (selectedPorts.length > 0 && currentIndex < selectedPorts.length) {
      return selectedPorts[currentIndex];
    }
    return selectedPorts[0] || null;
  }, [currentIndex, selectedPorts]);

  useEffect(() => {
    if (selectedPorts.length > 0 && currentIndex >= selectedPorts.length) {
      setCurrentIndex(0);
    }
  }, [selectedPorts.length, currentIndex]);

  if (!currentPort) {
    return null;
  }

  return (
    <div
      style={style}
      className={cn(
        'relative isolate max-h-screen animate-in fade-in slide-in-from-left-3 duration-200',
        className,
        selectedPorts.length === 2 && 'ml-3',
        selectedPorts.length >= 2 && 'ml-6',
      )}
    >
      <Card
        id="portinfo-card"
        className={cn(
          'relative flex flex-col min-w-[464px] max-w-[464px] h-screen z-10 shadow-md',
        )}
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
      >
        <CardHeader className="pb-2">
          <CardClose onClick={() => setSelectedPorts([])} />
          {portSelectionMode === 'nextport' && permissions.nextport && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="absolute right-12 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onClick={handleOpenPopup}
                >
                  <PiArrowSquareOut className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Open in new window</TooltipContent>
            </Tooltip>
          )}
          {multiplePortsSelected && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={cn(
                    'absolute top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    portSelectionMode === 'nextport' && permissions.nextport
                      ? 'right-20'
                      : 'right-12',
                  )}
                  onClick={goToNextCard}
                >
                  <PiArrowsClockwiseFill className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {`Go to next port: ${nextPortName}`}
              </TooltipContent>
            </Tooltip>
          )}
          {portSelectionMode === 'nextport' && permissions.nextport ? (
            <div className="flex min-w-0 items-center gap-3 pr-24">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
                <PiAnchor className="size-5" />
              </div>
              <div className="min-w-0">
                <CardTitle className="truncate text-lg font-medium text-muted-semibold">
                  {currentPort.name}
                </CardTitle>
                <p className="truncate text-sm font-medium text-primary">
                  {currentPort.locode}
                  {currentPort.country && ` · ${currentPort.country}`}
                </p>
              </div>
            </div>
          ) : (
            <CardTitle className="text-lg font-medium text-muted-semibold">
              {currentPort.name}
            </CardTitle>
          )}
        </CardHeader>

        <CardContent className="flex flex-col overflow-hidden space-y-6 h-full">
          {portSelectionMode === 'nextport' && permissions.nextport ? (
            <PortNextArrivalSummary port={currentPort} />
          ) : (
            <div className="grid gap-4">
              <PortIdentification port={currentPort}></PortIdentification>
            </div>
          )}
          {/* Show NextPort panel for 'nextport' mode */}
          {portSelectionMode === 'nextport' && permissions.nextport && (
            <PortDetailsPanel port={currentPort} />
          )}
          {portSelectionMode === 'nextport' && !permissions.nextport && (
            <MissingPermission
              entity={'nextport'}
              srcImage={'empty_list_bro.avif'}
            />
          )}
          {/* 'routes' mode shows only routes table (handled in useEffect above) */}
        </CardContent>
        <CardFooter className="">
          <PortActions port={currentPort}></PortActions>
        </CardFooter>
      </Card>
      <div className="absolute top-0 left-0">
        {selectedPorts.length >= 3 && (
          <Card
            id="stack-card-2"
            className={cn(
              'absolute z-1 min-w-[464px] max-w-[464px] h-screen -top-3 -left-6 shadow-sm cursor-default pointer-events-auto',
            )}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          ></Card>
        )}
        {selectedPorts.length >= 2 && (
          <Card
            id="stack-card-1"
            className={cn(
              'absolute z-2 min-w-[464px] max-w-[464px] h-screen -top-1.5 -left-3 shadow-sm cursor-default pointer-events-auto',
            )}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          ></Card>
        )}
      </div>
    </div>
  );
};

export function PortDetails({ className, style }: TargetDetailsMapProps) {
  const { selectedPorts, setSelectedPorts, portSelectionMode } =
    useSelectionState();
  const { setCenterComponent } = useMapPanelContext();
  const { setPort } = useRoutes();
  const permissions = usePermissions();

  // Get the current port (first selected)
  const currentPort = selectedPorts.length > 0 ? selectedPorts[0] : null;

  // Always draw port on map when selected
  useDrawPort(currentPort);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedPorts([]);
      }
    };

    window.addEventListener('keydown', onEscape);

    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [setSelectedPorts]);

  // Handle routes mode - show routes table without card
  useEffect(() => {
    if (!currentPort || !permissions.port || portSelectionMode !== 'routes') {
      return;
    }

    setPort(currentPort);
    setCenterComponent(CENTERCOMPONENTS.ROUTES);

    return () => {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    };
  }, [
    currentPort,
    permissions.port,
    portSelectionMode,
    setCenterComponent,
    setPort,
  ]);

  // Don't show card for 'routes' mode - only show the routes table
  if (selectedPorts.length === 0 || portSelectionMode === 'routes') {
    return null;
  }

  return <PortCardStack className={className} style={style} />;
}
